import React, { type FC, type PointerEvent, useEffect, useRef } from 'react';
import { INK_COLOR, trimToPng } from '../../../utility/signature';

export type DrawPadProps = {
  label: string;
  // Called with a transparent PNG data URL after each stroke, or null when cleared.
  onChange: (png: string | null) => void;
  height?: number;
};

const WIDTH = 700;

// A signature pad: draw with a mouse, trackpad or finger.
const DrawPad: FC<DrawPadProps> = ({ label, onChange, height = 200 }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawing = useRef(false);

  const context = () => {
    const ctx = canvasRef.current?.getContext('2d');
    if (ctx) {
      ctx.strokeStyle = INK_COLOR;
      ctx.lineWidth = 4;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
    }
    return ctx;
  };

  // Points are measured on the displayed canvas, then scaled to the canvas's real pixel size.
  const point = (event: PointerEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current as HTMLCanvasElement;
    const rect = canvas.getBoundingClientRect();
    return {
      x: (event.clientX - rect.left) * (canvas.width / (rect.width || canvas.width)),
      y: (event.clientY - rect.top) * (canvas.height / (rect.height || canvas.height)),
    };
  };

  const start = (event: PointerEvent<HTMLCanvasElement>) => {
    const ctx = context();
    if (!ctx) return;
    drawing.current = true;
    event.currentTarget.setPointerCapture?.(event.pointerId);
    const { x, y } = point(event);
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineTo(x + 0.01, y + 0.01); // a tap leaves a dot
    ctx.stroke();
  };

  const move = (event: PointerEvent<HTMLCanvasElement>) => {
    if (!drawing.current) return;
    const ctx = context();
    if (!ctx) return;
    const { x, y } = point(event);
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const finish = () => {
    if (!drawing.current) return;
    drawing.current = false;
    if (canvasRef.current) onChange(trimToPng(canvasRef.current));
  };

  const clear = () => {
    const canvas = canvasRef.current;
    canvas?.getContext('2d')?.clearRect(0, 0, canvas.width, canvas.height);
    onChange(null);
  };

  useEffect(() => clear, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div>
      <div className='flex items-center justify-between'>
        <span className='text-sm font-medium text-gray-900'>{label}</span>
        <button type='button' className='text-sm text-gray-500 hover:text-red-600' onClick={clear}>Clear</button>
      </div>
      <canvas
        ref={canvasRef}
        aria-label={`${label} drawing area`}
        width={WIDTH}
        height={height}
        className='mt-1 w-full cursor-crosshair rounded-md bg-white ring-1 ring-inset ring-gray-300'
        style={{ touchAction: 'none' }}
        onPointerDown={start}
        onPointerMove={move}
        onPointerUp={finish}
        onPointerLeave={finish}
        onPointerCancel={finish}
      />
    </div>
  );
};

export default DrawPad;
